import { isSignedOrder } from "@/domain/quant/signed-order";

export const ORDER_TABS = ["signed", "exec", "done"] as const;

export type OrderTab = (typeof ORDER_TABS)[number];

export type SuggestedStatus = "AWAITING_APPROVAL" | "APPROVED" | "IN_EXECUTION" | "EXECUTED";

export type StageAction = "quantify" | "confirm" | "revert";

export type ClientKind = "individual" | "corporation";

export interface ClientLink {
  key: string;
  kind: ClientKind;
  linkId: string;
  subjectId: string;
  advisorId: string | null;
}

export interface OrderRow {
  id: string;
  clientKey: string;
  subjectId: string | null;
  clientName: string | null;
  portfolioName: string | null;
  advisorId: string | null;
  advisorName: string | null;
  status: SuggestedStatus;
  createdAt: string | null;
  approvedAt: string | null;
  inExecutionAt: string | null;
  executedAt: string | null;
  pricedAt: string | null;
  transactionSetId: string | null;
  sourcePortfolioId: string | null;
  orderInstructionId: string | null;
  instruction: InstructionFacts | null;
  presentationFileId: string | null;
  orderListFileId: string | null;
}

export interface InstructionFacts {
  id: string;
  status: "DRAFT" | "PENDING" | "COMPLETED" | "CANCELED";
  documentFileId: string | null;
  sealedFileId: string | null;
  signers: { signed: number; total: number } | null;
}

export interface UnreadClient {
  clientKey: string;
  clientName: string | null;
  advisorId: string | null;
  advisorName: string | null;
  retrying: boolean;
}

export interface OrderFilter {
  advisorId: string;
  query: string;
}

export interface AdvisorOption {
  value: string;
  label: string;
}

export const ORDER_INSTRUCTIONS_PATH = "/order-instructions";

export function orderTabFromParam(param: string | undefined): OrderTab | null {
  if (param === undefined || param === "") return "signed";
  return ORDER_TABS.find((tab) => tab !== "signed" && tab === param) ?? null;
}

export function orderTabPath(tab: OrderTab): string {
  return tab === "signed" ? ORDER_INSTRUCTIONS_PATH : `${ORDER_INSTRUCTIONS_PATH}/${tab}`;
}

export function tabOfRow(row: Pick<OrderRow, "status" | "instruction">): OrderTab | null {
  if (row.status === "IN_EXECUTION") return "exec";
  if (row.status === "EXECUTED") return "done";
  return isSignedOrder(row) ? "signed" : null;
}

export function isSuggestedStatus(value: unknown): value is SuggestedStatus {
  return SUGGESTED_STATUSES.some((status) => status === value);
}

export function rowsInTab(
  rows: readonly OrderRow[],
  tab: OrderTab,
  filter: OrderFilter,
): OrderRow[] {
  return rows
    .filter((row) => tabOfRow(row) === tab && matchesFilter(row, filter))
    .sort((a, b) => instant(b.createdAt) - instant(a.createdAt));
}

export function unreadInView(unread: readonly UnreadClient[], filter: OrderFilter): UnreadClient[] {
  return unread.filter((client) => matchesFilter(client, filter));
}

export function advisorOptions(
  rows: readonly Pick<OrderRow, "advisorId" | "advisorName">[],
): AdvisorOption[] {
  const named = new Map<string, string>();
  for (const row of rows) {
    if (row.advisorId && row.advisorName) named.set(row.advisorId, row.advisorName);
  }
  return [...named]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label, "sv"));
}

export function clientLinkOfKey(key: string): { kind: ClientKind; linkId: string } | null {
  const at = key.indexOf(":");
  const kind = key.slice(0, at);
  const linkId = key.slice(at + 1);
  if ((kind !== "individual" && kind !== "corporation") || linkId === "") return null;
  return { kind, linkId };
}

function matchesFilter(
  row: Pick<OrderRow, "advisorId" | "clientName">,
  filter: OrderFilter,
): boolean {
  const query = filter.query.trim().toLocaleLowerCase("sv");
  return (
    (filter.advisorId === "" || row.advisorId === filter.advisorId) &&
    (query === "" || (row.clientName ?? "").toLocaleLowerCase("sv").includes(query))
  );
}

function instant(value: string | null): number {
  const parsed = value ? Date.parse(value) : Number.NaN;
  return Number.isFinite(parsed) ? parsed : 0;
}

const SUGGESTED_STATUSES: readonly SuggestedStatus[] = [
  "AWAITING_APPROVAL",
  "APPROVED",
  "IN_EXECUTION",
  "EXECUTED",
];
